import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import {
    getManagementTaskById,
    acceptManagementTaskOwnerTransfer,
    cancelManagementTaskOwnerTransfer,
    updateManagementTask,
    deleteManagementTask,
    deleteManagementSubTask
} from '../../services/api/implementation/impl';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import toast from 'react-hot-toast';
import { actsAsOwner } from './ownership';

import CreateSubtaskModal from './CreateSubtaskModal';
import EditSubtaskModal from './EditSubtaskModal';
import EditTaskModal from './EditTaskModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import AddDocumentModal from './AddDocumentModal';
import TaskActivityPanel, { ACTIVITY_TABS } from './TaskActivityPanel';
import TaskHistoryPanel from './TaskHistoryPanel';
import PermissionPanel from './PermissionPanel';
import QueryRoundsPanel from './QueryRoundsPanel';
import TransferOwnerModal from './TransferOwnerModal';
import OwnerTransferBanner, {
    pendingOwnerTransfer,
    canRequestOwnerTransfer
} from './OwnerTransferBanner';
import TaskBadges from './TaskBadges';
import TaskProgress from './TaskProgress';
import ReviewActions from './ReviewActions';
import AssigneeKindChip from './AssigneeKindChip';
import SubtaskReviewNotes from './SubtaskReviewNotes';
import { statusChipClass, TaskStatus } from './taskWorkflow';
import { nestSubtasks, subtaskInfoTooltip, subtaskStatusLabel, displayRootStatus, allSubtasksClosed } from './taskTree';
import {
    usesPermissionFlow,
    isLegalCaseTask,
    SubTaskPermissionType,
    subtaskTypeChipClass,
    kindLabel,
    kindChipClass
} from './permission';
import LegalCasePanel, { LegalCaseHeaderFacts } from './LegalCasePanel';
import LegalRecordsPanel from './LegalRecordsPanel';
import LegalCaseLogPanel from './LegalCaseLogPanel';
import LegalCaseExpensesPanel from './LegalCaseExpensesPanel';
import { caseStatusChipClass } from './legalCase';
import { useOwnerUserOptions } from './ownerOptions';
import { Info, Pencil, Trash2, UserCog } from 'lucide-react';
import UserAvatar from '../../../shared/components/ui/UserAvatar';
import { useSmartBack, resolveReturnTo } from '../../../shared/navigation';
import { formatRupees } from './budget';

const BUDGET_STATS = [
    { key: 'budget', label: 'Budget' },
    { key: 'allocated', label: 'Allocated' },
    { key: 'unallocated', label: 'Unallocated' },
    { key: 'actual', label: 'Actual' }
];

const ManagementTaskDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { collapsed } = useSidebar();
    const queryClient = useQueryClient();
    const { onBack: smartBack } = useSmartBack({
        fallback: '/builder/operations/managementTasks'
    });

    // The project page restores its tab from location.state and clears its
    // localStorage copy on unmount, and smartBack navigates without state — so
    // carry the tab back explicitly, otherwise back lands on Overview.
    const onBack = React.useCallback(() => {
        const returnTo = resolveReturnTo(location);
        const secondaryTab = location.state?.secondaryTab;
        if (returnTo && secondaryTab) {
            navigate(returnTo, { replace: true, state: { secondaryTab } });
            return;
        }
        smartBack();
    }, [location, navigate, smartBack]);

    const [tab, setTab] = React.useState('Overview');
    const [isSubtaskModalOpen, setIsSubtaskModalOpen] = React.useState(false);
    const [isEditSubtaskModalOpen, setIsEditSubtaskModalOpen] = React.useState(false);
    const [isEditTaskModalOpen, setIsEditTaskModalOpen] = React.useState(false);
    const [isAddDocModalOpen, setIsAddDocModalOpen] = React.useState(false);
    const [showTransferModal, setShowTransferModal] = React.useState(false);
    const [selectedSubtaskId, setSelectedSubtaskId] = React.useState(null);
    const [editingSubtask, setEditingSubtask] = React.useState(null);
    const [replyPrefill, setReplyPrefill] = React.useState({
        title: '',
        description: '',
        permissionType: '',
        permissionQueryId: '',
        newQuery: false
    });

    const { data: taskData, isLoading } = useQuery({
        queryKey: ['managementTask', id],
        queryFn: () => getManagementTaskById(id),
        enabled: !!id
    });

    const ownerOptions = useOwnerUserOptions(taskData?.data?.project_id);

    const task = taskData?.data;
    const parentStatus = displayRootStatus(task);
    const pending = pendingOwnerTransfer(task);
    const nestedSubtasks = nestSubtasks(task?.subTask);
    // Only the owner may delete; the API enforces it, this just hides buttons.
    // The owner, or the company's builder acting as owner.
    const isOwner = actsAsOwner(task);
    // Closed subtasks have no other action, but the owner can still delete them.
    const showActionColumn = isOwner || !allSubtasksClosed(nestedSubtasks);
    // Subtask types only exist on permission and compliance tasks.
    const showTypeColumn = usesPermissionFlow(task);

    const invalidateTask = () => {
        queryClient.invalidateQueries(['managementTask', id]);
        queryClient.invalidateQueries(['managementTasks']);
    };

    const acceptMutation = useMutation({
        mutationFn: (transferId) => acceptManagementTaskOwnerTransfer(id, transferId),
        onSuccess: () => {
            toast.success('You are now the owner');
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to accept transfer');
        }
    });

    const cancelMutation = useMutation({
        mutationFn: (transferId) => cancelManagementTaskOwnerTransfer(id, transferId),
        onSuccess: () => {
            toast.success('Owner transfer cancelled');
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to cancel transfer');
        }
    });

    const startDraftMutation = useMutation({
        mutationFn: () => updateManagementTask(id, { status: TaskStatus.PLANNED }),
        onSuccess: () => {
            toast.success('Task started');
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to start task');
        }
    });

    // { kind: 'task' | 'subtask', row }
    const [deleteTarget, setDeleteTarget] = React.useState(null);

    const deleteMutation = useMutation({
        mutationFn: (target) =>
            target.kind === 'task'
                ? deleteManagementTask(target.row.id)
                : deleteManagementSubTask(target.row.id),
        onSuccess: (_res, target) => {
            setDeleteTarget(null);
            if (target.kind === 'task') {
                toast.success('Task deleted');
                queryClient.removeQueries(['managementTask', id]);
                queryClient.invalidateQueries(['managementTasks']);
                onBack();
                return;
            }
            toast.success('Subtask deleted');
            setSelectedSubtaskId(null);
            invalidateTask();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to delete');
        }
    });

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const isLegalCase = isLegalCaseTask(task);
    // Named for the kind, so a compliance task never shows a Permission tab.
    const flowTab = kindLabel(task?.kind);
    // A court case has no subtasks, so the subtask tabs (Overview, Logs,
    // Expense, Documents) don't apply. Its Expenses tab holds the case's own
    // spend, and its Logs tab is the case's activity and notes, which covers
    // History too.
    const tabs = isLegalCase
        ? ['Case', 'Hearings', 'Follow-ups', 'Case documents', 'Expenses', 'Logs']
        : [
              'Overview',
              'History',
              ...(usesPermissionFlow(task) ? [flowTab, 'Query rounds'] : []),
              ...ACTIVITY_TABS
          ];
    // A task can lose (or rename) its flow tab if its kind changes while open.
    const activeTab = tabs.includes(tab) ? tab : tabs[0];

    const openCreateSubtask = () => {
        setReplyPrefill({ title: '', description: '', permissionType: '', permissionQueryId: '' });
        setIsSubtaskModalOpen(true);
    };

    return (
        <>
            <Loader isVisible={isLoading} />
            {isSubtaskModalOpen && (
                <CreateSubtaskModal
                    onClose={() => {
                        setIsSubtaskModalOpen(false);
                        setReplyPrefill({ title: '', description: '', permissionType: '', permissionQueryId: '' });
                    }}
                    parentTask={task}
                    initialTitle={replyPrefill.title}
                    initialDescription={replyPrefill.description}
                    initialPermissionType={replyPrefill.permissionType}
                    initialPermissionQueryId={replyPrefill.permissionQueryId}
                    newQuery={replyPrefill.newQuery}
                />
            )}
            {isEditSubtaskModalOpen && editingSubtask && (
                <EditSubtaskModal
                    subtask={editingSubtask}
                    parentTask={task}
                    onClose={() => {
                        setIsEditSubtaskModalOpen(false);
                        setEditingSubtask(null);
                    }}
                />
            )}
            {isEditTaskModalOpen && task && (
                // Project is settled on the task page, so the picker is hidden.
                <EditTaskModal
                    task={task}
                    lockedProjectId={task.project_id}
                    onClose={() => setIsEditTaskModalOpen(false)}
                />
            )}
            {isAddDocModalOpen && selectedSubtaskId && (
                <AddDocumentModal
                    subtaskId={selectedSubtaskId}
                    existingDocs={task?.subTask?.find(st => st.id === selectedSubtaskId)?.docs || []}
                    onClose={() => setIsAddDocModalOpen(false)}
                />
            )}
            {showTransferModal && task && (
                <TransferOwnerModal
                    task={task}
                    usersOptions={ownerOptions}
                    onClose={() => setShowTransferModal(false)}
                    onSuccess={invalidateTask}
                />
            )}
            {deleteTarget && (
                <ConfirmDeleteModal
                    title={deleteTarget.kind === 'task' ? 'Delete task' : 'Delete subtask'}
                    name={deleteTarget.row.title}
                    detail={
                        deleteTarget.kind === 'task'
                            ? 'All its subtasks, documents, activity and unapproved spend are deleted with it.'
                            : 'Its child subtasks, documents, activity and unapproved spend are deleted with it.'
                    }
                    busy={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(deleteTarget)}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
            <Layout>
                <div
                    className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-0 md:left-[76px] right-0' : 'left-0 md:left-64 right-0'
                        }`}>
                    <Header backArrow={true} onBack={onBack} data={{ name: 'Task Details' }} />
                </div>

                <div className="px-4 md:px-6 pb-10 mt-[6rem]">
                    {task ? (
                        <div className="space-y-6">
                            {/* Identity header, matching the Lead Details layout. */}
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-semibold text-sm flex-shrink-0">
                                        {task.title?.charAt(0)?.toUpperCase() || 'T'}
                                    </div>
                                    <p className="font-bold text-gray-900 text-2xl md:text-[30px] leading-tight">
                                        {task.title}
                                    </p>
                                    <span className="text-gray-500 text-base font-medium">
                                        #{task.id}
                                        {isLegalCase && task.legalCase?.caseCode ? ` · ${task.legalCase.caseCode}` : ''}
                                    </span>
                                    {isLegalCase && task.legalCase ? (
                                        // The case status drives the task status, so it is the one shown.
                                        <span
                                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm leading-none font-semibold ${caseStatusChipClass(task.legalCase.caseStatus)}`}>
                                            {task.legalCase.caseStatus}
                                        </span>
                                    ) : (
                                        <span
                                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm leading-none font-semibold ${statusChipClass(parentStatus)}`}>
                                            {parentStatus || '---'}
                                        </span>
                                    )}
                                    {(parentStatus === TaskStatus.DRAFT || task.status === TaskStatus.DRAFT) && (
                                        <button
                                            type="button"
                                            disabled={startDraftMutation.isPending}
                                            onClick={() => startDraftMutation.mutate()}
                                            className="bg-[#346C75] text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-[#2a565e] disabled:opacity-50"
                                        >
                                            {startDraftMutation.isPending ? 'Starting...' : 'Start'}
                                        </button>
                                    )}
                                    <span
                                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm leading-none font-semibold ${kindChipClass(task.kind)}`}>
                                        {kindLabel(task.kind)}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label="Edit task"
                                        title="Edit task"
                                        className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5EAF1] bg-white text-[#346C75] shadow-sm transition-colors hover:bg-[#F7FAFB]"
                                        onClick={() => setIsEditTaskModalOpen(true)}
                                    >
                                        <Pencil size={18} strokeWidth={2} />
                                    </button>
                                    {canRequestOwnerTransfer(task) && (
                                        <button
                                            type="button"
                                            aria-label="Transfer owner"
                                            title="Transfer owner"
                                            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5EAF1] bg-white text-[#346C75] shadow-sm transition-colors hover:bg-[#F7FAFB]"
                                            onClick={() => setShowTransferModal(true)}
                                        >
                                            <UserCog size={18} strokeWidth={2} />
                                        </button>
                                    )}
                                    {isOwner && (
                                        <button
                                            type="button"
                                            aria-label="Delete task"
                                            title="Delete task"
                                            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5EAF1] bg-white text-[#B42318] shadow-sm transition-colors hover:bg-[#FEE9EC]"
                                            onClick={() => setDeleteTarget({ kind: 'task', row: task })}
                                        >
                                            <Trash2 size={18} strokeWidth={2} />
                                        </button>
                                    )}
                                    <TaskBadges badges={task.badges} className="flex flex-wrap gap-1" />
                                </div>

                                {task.description && (
                                    <p className="text-gray-500 text-sm">{task.description}</p>
                                )}

                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-gray-700">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-800 text-sm">
                                        <UserAvatar name={task.owner?.name} size={18} />
                                        <span className="font-semibold">Owner:</span>
                                        <span className="font-medium">{task.owner?.name || '---'}</span>
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-gray-400">•</span>
                                        <span className="font-medium">
                                            {isLegalCase ? 'Target' : 'Due'} {formatDate(task.dueDate)}
                                        </span>
                                    </span>
                                    {isLegalCase && <LegalCaseHeaderFacts legalCase={task.legalCase} />}
                                    {!isLegalCase && (
                                        // Progress comes from subtasks, and a court case has none.
                                        <span className="w-52">
                                            <TaskProgress progress={task.progress} />
                                        </span>
                                    )}
                                    {!isLegalCase &&
                                        BUDGET_STATS.map(({ key, label }) => (
                                            <span key={key} className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-400">•</span>
                                                <span className="text-gray-500">{label}:</span>
                                                <span className="font-semibold text-gray-900">
                                                    {formatRupees(task[key])}
                                                </span>
                                            </span>
                                        ))}
                                </div>
                            </div>

                            <div className="border-b border-gray-200">
                                <div className="flex items-center gap-5 overflow-x-auto whitespace-nowrap">
                                    {tabs.map((name) => (
                                        <button
                                            key={name}
                                            type="button"
                                            onClick={() => setTab(name)}
                                            className={`text-base pb-2.5 ${
                                                name === activeTab
                                                    ? 'text-teal-700 border-b-2 border-teal-700 font-semibold'
                                                    : 'text-gray-700 font-medium hover:text-gray-900'
                                            }`}>
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <OwnerTransferBanner
                                task={task}
                                onCancel={() => pending && cancelMutation.mutate(pending.id)}
                                onAccept={() => pending && acceptMutation.mutate(pending.id)}
                                onDecline={() => pending && cancelMutation.mutate(pending.id)}
                                busy={acceptMutation.isPending || cancelMutation.isPending}
                            />

                            {activeTab === 'History' && <TaskHistoryPanel task={task} />}

                            {activeTab === 'Case' && <LegalCasePanel task={task} />}
                            {activeTab === 'Hearings' && <LegalRecordsPanel task={task} kind="hearing" />}
                            {activeTab === 'Follow-ups' && <LegalRecordsPanel task={task} kind="followUp" />}
                            {activeTab === 'Case documents' && (
                                <LegalRecordsPanel task={task} kind="document" />
                            )}
                            {isLegalCase && activeTab === 'Expenses' && (
                                <LegalCaseExpensesPanel task={task} />
                            )}

                            {activeTab === flowTab && (
                                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
                                    <PermissionPanel task={task} embedded />
                                </div>
                            )}

                            {activeTab === 'Query rounds' && (
                                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
                                    <QueryRoundsPanel
                                        task={task}
                                        embedded
                                        onAddQuery={() => {
                                            const count = (task.permissionQueries || []).length;
                                            setReplyPrefill({
                                                title: `Reply to query #${count + 1}`,
                                                description: '',
                                                permissionType: SubTaskPermissionType.QUERY_RAISED,
                                                permissionQueryId: '',
                                                newQuery: true
                                            });
                                            setIsSubtaskModalOpen(true);
                                        }}
                                        onCreateReply={(query, index) => {
                                            setReplyPrefill({
                                                title: `Reply to query #${index + 1}`,
                                                description: query?.text || '',
                                                permissionType: SubTaskPermissionType.QUERY_RAISED,
                                                permissionQueryId: query?.id || ''
                                            });
                                            setIsSubtaskModalOpen(true);
                                        }}
                                    />
                                </div>
                            )}

                            {isLegalCase && activeTab === 'Logs' && <LegalCaseLogPanel task={task} />}

                            {!isLegalCase && ACTIVITY_TABS.includes(activeTab) && (
                                <TaskActivityPanel
                                    task={task}
                                    tab={activeTab}
                                    targetId={selectedSubtaskId}
                                    onTargetChange={setSelectedSubtaskId}
                                    onOpenAddDoc={(subtaskId) => {
                                        setSelectedSubtaskId(subtaskId);
                                        setIsAddDocModalOpen(true);
                                    }}
                                />
                            )}

                            {activeTab === 'Overview' && (
                            <>
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b flex items-center justify-between gap-3">
                                    <h2 className="text-sm font-semibold text-gray-800">Subtasks</h2>
                                    {parentStatus !== TaskStatus.COMPLETED &&
                                        parentStatus !== TaskStatus.CANCELLED && (
                                        <button
                                            type="button"
                                            className="bg-[#346C75] text-white px-4 py-2 rounded hover:bg-[#2a565e]"
                                            onClick={openCreateSubtask}
                                        >
                                            + Create Subtask
                                        </button>
                                    )}
                                </div>
                                <div className="overflow-x-auto overflow-y-hidden">
                                    <table className="min-w-full text-left">
                                        <thead className="bg-gray-50 text-gray-600">
                                            <tr>
                                                <th className="px-6 py-3 font-medium text-sm">Title</th>
                                                <th className="px-6 py-3 font-medium text-sm">Assigned To</th>
                                                <th className="px-6 py-3 font-medium text-sm">Approver</th>
                                                {showTypeColumn && (
                                                    <th className="px-6 py-3 font-medium text-sm">Type</th>
                                                )}
                                                <th className="px-6 py-3 font-medium text-sm">Status</th>
                                                <th className="px-6 py-3 font-medium text-sm">Allocation</th>
                                                <th className="px-6 py-3 font-medium text-sm">Actual</th>
                                                <th className="px-6 py-3 font-medium text-sm">Dates</th>
                                                {showActionColumn && (
                                                    <th className="px-6 py-3 font-medium text-sm">Action</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {nestedSubtasks.length > 0 ? (
                                                nestedSubtasks.map((subtask) => (
                                                    <tr
                                                        key={subtask.id}
                                                        className="hover:bg-gray-50"
                                                    >
                                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                            <div className="flex items-center gap-1.5">
                                                                <span>{subtask.title}</span>
                                                                <div
                                                                    className="relative inline-flex group shrink-0"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <Info
                                                                        size={14}
                                                                        className="text-gray-400 hover:text-[#346C75] cursor-help"
                                                                        aria-label="View description and review notes"
                                                                    />
                                                                    <div className="absolute left-full top-0 ml-1.5 px-2 py-1.5 text-xs font-normal bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-30 max-w-[240px] whitespace-pre-wrap pointer-events-none">
                                                                        {subtaskInfoTooltip(subtask)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <SubtaskReviewNotes subtask={subtask} />
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">
                                                            <div className="flex items-center gap-2">
                                                                <UserAvatar name={subtask.assignedTo?.name} size={28} />
                                                                <AssigneeKindChip kind={subtask.assigneeKind} name={subtask.assignedTo?.name} />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">
                                                            <div className="flex items-center gap-2">
                                                                {/* No approver: the owner reviews it (see reviewerId). */}
                                                                <UserAvatar name={subtask.approver?.name || task.owner?.name} size={28} />
                                                                <span>{subtask.approver?.name || task.owner?.name || '---'}</span>
                                                                {!subtask.approver?.name && task.owner?.name && (
                                                                    <span className="text-xs text-gray-400">(Owner)</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        {showTypeColumn && (
                                                            <td className="px-6 py-4">
                                                                {subtask.permissionType ? (
                                                                    <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${subtaskTypeChipClass(subtask.permissionType)}`}>
                                                                        {subtask.permissionType}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-sm text-gray-400">---</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusChipClass(subtaskStatusLabel(task, subtask))}`}>
                                                                {subtaskStatusLabel(task, subtask)}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">{formatRupees(subtask.budget)}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-700">{formatRupees(subtask.actual)}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            <div className="flex flex-col text-xs">
                                                                <span>Start: {formatDate(subtask.plannedStartDate)}</span>
                                                                <span>End: {formatDate(subtask.plannedEndDate)}</span>
                                                                <span>Duration: {subtask.duration} day{subtask.duration === 1 ? '' : 's'}</span>
                                                            </div>
                                                        </td>
                                                        {showActionColumn && (
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                {subtask.status !== TaskStatus.COMPLETED && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingSubtask(subtask);
                                                                        setIsEditSubtaskModalOpen(true);
                                                                    }}
                                                                    className="text-gray-400 hover:text-[#346C75] p-1.5 rounded-md hover:bg-gray-100"
                                                                    title="Edit Subtask"
                                                                >
                                                                    <Pencil size={15} />
                                                                </button>
                                                                )}
                                                                {isOwner && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setDeleteTarget({ kind: 'subtask', row: subtask });
                                                                    }}
                                                                    className="text-gray-400 hover:text-[#B42318] p-1.5 rounded-md hover:bg-[#FEE9EC]"
                                                                    title="Delete Subtask"
                                                                    aria-label="Delete Subtask"
                                                                >
                                                                    <Trash2 size={15} />
                                                                </button>
                                                                )}
                                                                <ReviewActions
                                                                    task={task}
                                                                    subtask={subtask}
                                                                    onDone={invalidateTask}
                                                                    showWaive
                                                                />
                                                            </div>
                                                        </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7 + (showActionColumn ? 1 : 0) + (showTypeColumn ? 1 : 0)} className="px-6 py-10 text-center text-gray-500">
                                                        No subtasks found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </>
                            )}
                        </div>
                    ) : (
                        !isLoading && (
                            <div className="flex flex-col items-center justify-center pt-20">
                                <img
                                    src={noDataIcon}
                                    alt="No Data"
                                    className="w-[15rem] h-[15rem] object-contain opacity-50"
                                />
                                <p className="text-lg text-gray-500 mt-4">Task not found</p>
                                <button
                                    onClick={() => navigate(-1)}
                                    className="mt-4 text-blue-600 hover:underline"
                                >
                                    Go Back
                                </button>
                            </div>
                        )
                    )}
                </div>
            </Layout>
        </>
    );
};

export default ManagementTaskDetails;
